import { Transaction, TransactionActivity } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Combobox,
  DateInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  SimpleSelect,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';

import { JobSelector } from '../../../../../components/JobSelector';
import { WaiverTypes } from '../../../../../constants';
import { useAuth } from '../../../../../context/AuthContext';
import { queryKeys } from '../../../../../hooks/react-query/constants';
import { useTimesheetDepartmentListQuery } from '../../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useTransactionActivityCreateMutation } from '../../../../../hooks/react-query/useTransactionActivityQuery';
import {
  useTransactionCreateMutation,
  useTransactionImmediateGetQuery,
} from '../../../../../hooks/react-query/useTransactionQuery';
import { useBatchUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import {
  useImmediateVendorGetQuery,
  useVendorBatchQuery,
} from '../../../../../hooks/react-query/useVendorQuery';
import { uploadPhotoToExistingTransaction } from '../../../../../tools/helpers';
import { Form as OldForm } from '../../../Form';
import { Modal } from '../../../Modal';
import { TransactionAccountComboboxPicker } from '../../../Pickers/newPickers/QueryPickerV2';
import { techniciansUserFilter } from '../../../Pickers/newPickers/utils';
import { type PopupType } from '../../reducer';
import { getPaymentTermsDisplayText } from '../constants/paymentTerms';
import { type FormAlert, FormAlertArea } from '../FormAlertArea';
import {
  clearDuplicateInvoiceAlert,
  getDuplicateInvoiceAlert,
  getDuplicateInvoiceFilter,
  VERIFY_INVOICE_FAILED_ALERT,
} from '../utils/duplicateInvoice';
import { calculatePaymentDueDate, parsePaymentDueDate } from '../utils/paymentCalculations';
import { VendorField } from '../VendorField';
import { type TransactionCreateSchemeType, useTransactionCreateForm } from './utils';

export type FileChangeData = {
  file: string | ArrayBuffer | null;
  filename: string;
};
export type OnFileChangeHandler = (data: FileChangeData) => void;

export const FileInput = ({
  onFileChange,
  onChange,
  ...rest
}: {
  onFileChange: OnFileChangeHandler;
} & Omit<ComponentProps<typeof Input>, 'type'>) => {
  const combinedOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    const file = e.target.files?.[0];
    if (file) {
      const filename = file.name;
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        onFileChange({ file: fileReader.result, filename });
      };
    }
  };

  return <Input {...rest} type="file" onChange={combinedOnChange} />;
};

export const TransactionCreateForm = ({ onSave }: { onSave?: () => void }) => {
  const loggedUserId = useAuth().user.id;
  const form = useTransactionCreateForm();

  const queryClient = useQueryClient();
  const transactionCreateMutation = useTransactionCreateMutation();
  const transactionActivityCreateMutation = useTransactionActivityCreateMutation();

  const vendorGetQuery = useImmediateVendorGetQuery();
  const transactionClientServiceGetQuery = useTransactionImmediateGetQuery();

  // Watch for changes to vendorId and timestamp to auto-calculate payment due date
  const vendorId = useWatch({ control: form.control, name: 'vendorId' });
  const timestamp = useWatch({ control: form.control, name: 'timestamp' });

  const vendorsQuery = useVendorBatchQuery({
    filter: {
      isActive: 1,
      withoutLimit: true,
      orderBy: 'vendor_name',
    },
  });

  // Get selected vendor for payment terms calculation
  const selectedVendor = vendorsQuery.data?.results.find((v) => v.id === vendorId);

  // Auto-calculate payment due date when vendor or timestamp changes
  useEffect(() => {
    if (selectedVendor?.netTerms && timestamp) {
      const currentPaymentDueDate = form.getValues('paymentDueDate');

      // Only auto-calculate if no payment due date is set or if it's empty
      if (!currentPaymentDueDate) {
        const calculatedDueDate = calculatePaymentDueDate(timestamp, selectedVendor.netTerms);
        form.setValue('paymentDueDate', calculatedDueDate, { shouldDirty: true });
      }
    }
  }, [selectedVendor, timestamp, form]);

  const handleCheckOrderNumber = useCallback(
    async (orderNumber: string) => {
      if (orderNumber && orderNumber != '' && vendorId) {
        let splitOrderNumber = orderNumber.split('.')[0];
        splitOrderNumber = splitOrderNumber.split('-')[0];

        try {
          const result = await transactionClientServiceGetQuery({
            invoiceNumber: `%${splitOrderNumber}%`,
            vendorId,
            vendorCategory: "'PickTicket','Receipt','Invoice'",
            isActive: 1,
          });
          if (result?.id && result?.id != 0) {
            setFormAlert({
              variant: 'warning',
              title: 'Order # matches an existing Invoice # for this vendor',
              message:
                'We have detected that this Order# is an Existing Invoice Number for this vendor. Please be sure this is Correct',
            });
          }
        } catch (err) {
          console.error('Failed to check Order number', err);
        }
      }
    },
    [transactionClientServiceGetQuery, vendorId],
  );

  const findDuplicateInvoice = useCallback(
    async (invoiceNumber: string, vendorId: number) => {
      const filter = getDuplicateInvoiceFilter(invoiceNumber, vendorId);
      if (!filter) return null;
      const result = await transactionClientServiceGetQuery(filter);
      return result?.id ? result : null;
    },
    [transactionClientServiceGetQuery],
  );

  const handleCheckInvoiceNumber = useCallback(
    async (invoiceNumber: string) => {
      try {
        const duplicate = await findDuplicateInvoice(invoiceNumber, vendorId);
        setFormAlert(
          duplicate ? getDuplicateInvoiceAlert(invoiceNumber) : clearDuplicateInvoiceAlert,
        );
      } catch (err) {
        console.error('Failed to check Invoice number', err);
      }
    },
    [findDuplicateInvoice, vendorId],
  );

  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
    select(data) {
      return (
        data.results.map((technician) => ({
          label: `${technician.firstname} ${technician.lastname} (ID: ${technician.id})`,
          value: technician.id.toString(),
        })) ?? []
      );
    },
  });

  const timesheetDepartmentQuery = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
    select(data) {
      return (
        data?.results.map((department) => ({
          label: `${department.value} | ${department.description}`,
          value: department.id.toString(),
        })) ?? []
      );
    },
  });

  const [isImageTypePopupOpen, setIsImageTypePopupOpen] = useState(false);
  const [imageWaiverTypeFormData, setImageWaiverTypeFormData] = useState<PopupType>({
    documentType: 'Receipt',
    invoiceWaiverType: 0,
  });
  const [fileData, setFileData] = useState<FileChangeData | null>(null);
  const [savedTransaction, setSavedTransaction] = useState<Transaction | null>(null);
  const [isSavingTransactionFile, setIsSavingTransactionFile] = useState(false);

  const [jobSelectorKey, setJobSelectorKey] = useState(0);

  const [formAlert, setFormAlert] = useState<FormAlert | null>(null);

  const handleResetForm = useCallback(() => {
    form.reset();
    setFormAlert(null);
    setJobSelectorKey((prev) => prev + 1);
  }, [form]);

  const handleSave = useCallback(() => {
    onSave?.();
    form.reset();
    setFormAlert(null);
    setFileData(null);
  }, [form, onSave]);

  const [isSaving, setIsSaving] = useState(false);

  const onFormSave: SubmitHandler<TransactionCreateSchemeType> = useCallback(
    async (submitValues) => {
      setIsSaving(true);
      try {
        const duplicate = await findDuplicateInvoice(
          submitValues.invoiceNumber,
          submitValues.vendorId,
        );
        if (duplicate) {
          setFormAlert(getDuplicateInvoiceAlert(submitValues.invoiceNumber));
          setIsSaving(false);
          return;
        }
      } catch (err) {
        console.error('Failed to check Invoice number', err);
        setFormAlert(VERIFY_INVOICE_FAILED_ALERT);
        setIsSaving(false);
        return;
      }
      const vendorName = submitValues.vendorId
        ? (await vendorGetQuery({ id: Number(submitValues.vendorId) }))?.vendorName
        : undefined;
      const newTxn = Transaction.create({
        ...submitValues,
        timestamp: format(submitValues.timestamp, 'yyyy-MM-dd HH:mm:ss'),
        notes: submitValues.notes
          ? submitValues.notes
          : `Order Number-${submitValues.orderNumber},Job Number-${submitValues.jobId}`,
        vendorCategory: 'Receipt',
        statusId: 2, // Pending
        assignedEmployeeId: loggedUserId,
        vendor: vendorName,
        paymentDueDate: submitValues.paymentDueDate || format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      });
      let res: Transaction | null = null;
      try {
        res = await transactionCreateMutation.mutateAsync(newTxn);
        queryClient.invalidateQueries({
          queryKey: [queryKeys.transaction.root],
        });
        if (fileData) {
          // if fileName is not empty, we need to open a popup to specify the type of the document
          setIsImageTypePopupOpen(true);
          setSavedTransaction(res);
        } else {
          handleSave();
        }
      } catch (err) {
        console.error(`An error occurred while creating a transaction: ${err}`);
        const log = TransactionActivity.create({
          timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          userId: loggedUserId,
          statusId: 2,
          isActive: 1,
          description: `ERROR : An error occurred while creating a transaction: ${err}.`,
        });
        try {
          await transactionActivityCreateMutation.mutateAsync(log);
        } catch (err) {
          console.error(
            `An error occurred while uploading a transaction activity log for a transaction: ${err}`,
          );
        } finally {
          setIsSaving(false);
        }
        return;
      }

      // Without an id there is no transaction to attach the creation log to.
      if (res?.id) {
        const log = TransactionActivity.create({
          timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          userId: loggedUserId,
          statusId: 2,
          isActive: 1,
          transactionId: res.id,
          description: `Transaction created with id: ${res.id}`,
        });
        transactionActivityCreateMutation.mutate(log);
      }
      setIsSaving(false);
    },
    [
      fileData,
      findDuplicateInvoice,
      handleSave,
      loggedUserId,
      queryClient,
      transactionActivityCreateMutation,
      transactionCreateMutation,
      vendorGetQuery,
    ],
  );

  return (
    <>
      <Form {...form}>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          onSubmit={form.handleSubmit(onFormSave)}
        >
          <FormField
            control={form.control}
            name="timestamp"
            render={({ field }) => {
              const maxDate = new Date();
              maxDate.setFullYear(maxDate.getFullYear() + 1);
              return (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormMessage />
                  <DateInput value={field.value} onChange={field.onChange} maxDate={maxDate} />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="orderNumber"
            render={({ field }) => {
              const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                field.onBlur();
                if (e.target.value != '') {
                  handleCheckOrderNumber(e.target.value);
                }
              };
              return (
                <FormItem>
                  <FormLabel>Purchase Order #</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input inputMode="numeric" {...field} onBlur={onBlur} />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="invoiceNumber"
            render={({ field }) => {
              const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                field.onBlur();
                handleCheckInvoiceNumber(e.target.value);
              };
              return (
                <FormItem>
                  <FormLabel>Invoice #</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input inputMode="numeric" {...field} onBlur={onBlur} />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="ownerId"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Purchaser/PM</FormLabel>
                  <FormMessage />
                  <Combobox
                    onChange={field.onChange}
                    value={field.value?.toString() ?? ''}
                    emptyLabel={'Select Purchaser/PM'}
                    disabled={techniciansQuery.isPending}
                    values={techniciansQuery.data ?? []}
                    placeholder="Search Purchaser/PM"
                  />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="jobId"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormLabel>Job #</FormLabel>
                <FormMessage />
                <JobSelector
                  key={jobSelectorKey}
                  value={value}
                  onChange={onChange}
                  onJobSelect={(event) => {
                    if (
                      event.departmentId &&
                      event.departmentId !== form.getValues('departmentId')
                    ) {
                      form.setValue('departmentId', event.departmentId, { shouldDirty: true });
                    }
                  }}
                  omitArchivedJobs={true}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="departmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Department</FormLabel>
                <FormMessage />
                <SimpleSelect
                  disabled={!timesheetDepartmentQuery.isSuccess}
                  placeholder={'Select Department'}
                  selectedValue={field.value.toString()}
                  onChange={field.onChange}
                  values={timesheetDepartmentQuery.data ?? []}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="costCenterId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost Center</FormLabel>
                <FormMessage />
                <TransactionAccountComboboxPicker
                  renderItem={(tA) => `${tA.id}-${tA.shortDescription}`}
                  selected={field.value.toString()}
                  onSelect={(tA) => field.onChange(tA?.id ?? 0)}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormMessage />
                <FormControl>
                  <Input type="number" step="0.01" inputMode="numeric" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vendorId"
            render={({ field }) => (
              <VendorField
                value={field.value}
                onChange={field.onChange}
                disabled={field.disabled}
              />
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormMessage />
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="paymentDueDate"
            render={({ field }) => {
              const paymentDueDate = parsePaymentDueDate(field.value);
              const paymentTermsText = selectedVendor?.netTerms
                ? getPaymentTermsDisplayText(selectedVendor.netTerms)
                : 'None';

              return (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    Payment Due Date
                    <Badge variant="outline" className="text-xs">
                      {paymentTermsText}
                    </Badge>
                  </FormLabel>
                  <FormMessage />
                  <FormControl>
                    <DateInput
                      value={paymentDueDate || new Date()}
                      onChange={(date) => {
                        const dateString = date ? format(date, 'yyyy-MM-dd HH:mm:ss') : '';
                        field.onChange(dateString);
                      }}
                      maxDate={(() => {
                        const maxDate = new Date();
                        maxDate.setFullYear(maxDate.getFullYear() + 1);
                        return maxDate;
                      })()}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <div className="space-y-2">
            <Label htmlFor="document">Add Image / Document</Label>
            <FileInput tabIndex={0} onFileChange={setFileData} />
          </div>

          <div className="flex items-center justify-center gap-2 self-end justify-self-start">
            <Button disabled={isSaving} isLoading={isSaving} tabIndex={0} type="submit">
              Create
            </Button>
            <Button onClick={handleResetForm} variant="outline" type="reset">
              Reset
            </Button>
          </div>

          <FormAlertArea alert={formAlert} />
        </form>
      </Form>
      {isImageTypePopupOpen && savedTransaction && (
        <Modal open onClose={() => setIsImageTypePopupOpen(false)}>
          <OldForm<PopupType>
            disabled={!savedTransaction}
            // key={state.imageWaiverTypeFormData.toString()}
            title={'Specify Type for Document - ' + fileData?.filename}
            onChange={setImageWaiverTypeFormData}
            schema={[
              [
                {
                  name: 'documentType',
                  label: 'Document Type',
                  options: ['PickTicket', 'Receipt', 'Invoice'],
                  required: true,
                },
                {
                  name: 'invoiceWaiverType',
                  label: 'Waiver Type',
                  options: WaiverTypes,
                  required: imageWaiverTypeFormData.documentType == 'Invoice',
                  invisible: imageWaiverTypeFormData.documentType !== 'Invoice',
                },
              ],
            ]}
            onSave={async (saved) => {
              if (!fileData) {
                console.error(`Not proceeding with image save. No file data found.`);
                return;
              }
              const { file } = fileData;
              if (typeof file !== 'string') {
                console.error('Not proceeding with image save. File is not a string.');
                return;
              }
              try {
                setIsSavingTransactionFile(true);
                await uploadPhotoToExistingTransaction(
                  fileData.filename,
                  saved.documentType,
                  file,
                  savedTransaction,
                  loggedUserId,
                  saved.invoiceWaiverType,
                );
              } catch (err) {
                console.error('Failed to upload photo to existing transaction', err);
              } finally {
                setIsSavingTransactionFile(false);
              }
              setIsImageTypePopupOpen(false);
              handleSave();
              queryClient.invalidateQueries({
                queryKey: [queryKeys.transaction.root],
              });
            }}
            onClose={() => {
              handleSave();
              setIsImageTypePopupOpen(false);
            }}
            submitLabel="Upload"
            submitDisabled={isSavingTransactionFile}
            data={imageWaiverTypeFormData}
          />
        </Modal>
      )}
    </>
  );
};
